import React, { useEffect } from "react";
import { ResponsiveHeatMap } from "@nivo/heatmap";
import { Box, Button, Flex, Tag, TagLabel, Text, Wrap, WrapItem } from "@chakra-ui/react";
import { ArrowRight, ArrowUp, PencilSimple } from "@phosphor-icons/react";
import { getGapAnalysisDashboardData, getQuestionGroupList, STAGETYPE_LABEL } from "../../dashboardDataUtils";
import { useSearchParams } from "react-router-dom";
import SearchString from "search-string";
import { STATUSFIELD_VALUE_LABEL } from "../../../IssbDataInput/components/AdditionalStatuses";
import _ from "lodash";
import { gotoFilteredQuestionList } from "../AIDashboard/AIDashboard";
import { useNavigate } from "react-router-dom";
import { nivo_theme } from "../chart-utils";

const GapAnalysis = ({ questions }) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [data, setData] = React.useState([]);
  const [groupFilter, setGroupFilter] = React.useState([]);
  const [editing, setEditing] = React.useState(false);

  const questionGroups = getQuestionGroupList(questions);

  // Bleurgh: map condensed-stage-type to stageNames
  const stageNamesForStageTypeCondensed = {};
  questions.forEach((question) => {
    const stageTypeCondensed = STAGETYPE_LABEL[question.stageType];
    let stageNames = stageNamesForStageTypeCondensed[stageTypeCondensed];
    if (!stageNames) {
      stageNames = [];
      stageNamesForStageTypeCondensed[stageTypeCondensed] = stageNames;
    }
    if (!stageNames.includes(question.stage)) {
      stageNames.push(question.stage);
    }
  });

  const handleClick = (stageType, confidence) => {
    const searchString = SearchString.parse("");
    const stageNames = stageNamesForStageTypeCondensed[stageType];
    const stageFilter = stageNames.join(","); // comma-separated list
    searchString.addEntry("stage", stageFilter);
    searchString.addEntry("confidence", confidence);
    // if groupfilter is not equal to all groups, add group filter to search string
    if (groupFilter.length !== questionGroups.length) {
      groupFilter.map((group) => searchString.addEntry("questionGroup", group));
    }
    // TODO support all / my Qs filter
    const newParams = new URLSearchParams(searchParams);
    newParams.set("q", searchString.toString());
    gotoFilteredQuestionList(navigate, null, null, newParams);
  };

  const handleGroupFilter = (group) => {
    if (group === "__ALL__") {
      if (groupFilter.length === questionGroups.length) {
        setGroupFilter([]);
      } else {
        setGroupFilter(questionGroups);
      }
      return;
    }

    if (groupFilter.includes(group)) {
      setGroupFilter(groupFilter.filter((g) => g !== group));
    } else {
      setGroupFilter([...groupFilter, group]);
    }
  };

  useEffect(() => {
    setGroupFilter(getQuestionGroupList(questions));
  }, [questions]);

  useEffect(() => {
    setData(getGapAnalysisDashboardData(questions, groupFilter));
  }, [questions, groupFilter]);

  const CustomTooltip = ({ stageType, confidence, value }) => (
    <Box borderRadius="lg" bg="pureWhite" border="1px solid" borderColor="lime" px={3} py={2}>
      <Text fontSize="sm" color="darkTeal">
        <Text as="span" fontWeight="bold" color="lime">Stage:</Text> {STAGETYPE_LABEL[stageType] || stageType}
      </Text>
      <Text fontSize="sm" color="darkTeal">
        <Text as="span" fontWeight="bold">Confidence:</Text> {STATUSFIELD_VALUE_LABEL.confidence[confidence]}
      </Text>
    </Box>
  );

  return (
    <Flex direction="column" p={4} gap={4} w="100%">
      {/* Group Filter: "All groups" label with Edit, or toggleable pills when editing */}
      {groupFilter.length === questionGroups.length && !editing ? (
        <Wrap spacing={2} align="center">
          <WrapItem>
            <Tag
              size="sm"
              borderRadius="full"
              bg="lightLime"
              border="1px solid"
              borderColor="lime"
              px={3}
              py={1}
              cursor="pointer"
              onClick={() => setEditing(true)}
            >
              <TagLabel>All groups selected</TagLabel>
              <Box as="span" ml={2} display="inline-flex"><PencilSimple size={12} /></Box>
            </Tag>
          </WrapItem>
        </Wrap>
      ) : (
        <Wrap spacing={2} align="center">
          {questionGroups.map((group) => {
            const isSelected = groupFilter.includes(group);
            return (
              <WrapItem key={group}>
                <Tag
                  size="sm"
                  borderRadius="full"
                  bg={isSelected ? "lightLime" : "gray.100"}
                  border="1px solid"
                  borderColor={isSelected ? "lime" : "gray.300"}
                  color={isSelected ? "inherit" : "gray.400"}
                  px={3}
                  py={1}
                  cursor="pointer"
                  onClick={() => handleGroupFilter(group)}
                >
                  <TagLabel noOfLines={1}>{group}</TagLabel>
                  <Box as="span" ml={1} fontSize="xs">{isSelected ? "✕" : "+"}</Box>
                </Tag>
              </WrapItem>
            );
          })}

          <WrapItem>
            <Button
              variant="link"
              size="xs"
              color="gray.500"
              fontWeight="normal"
              onClick={() => {
                setGroupFilter(questionGroups);
                setEditing(false);
              }}
            >
              Select all
            </Button>
          </WrapItem>
        </Wrap>
      )}

      <Flex height="500px" w="100%">
        <Box flex="1" minW={0}>
          <ResponsiveHeatMap
            data={data}
            margin={{ top: 60, right: 20, bottom: 20, left: 120 }}
            valueFormat=".0f"
            axisTop={{
              tickSize: 0,
              tickPadding: 0,
              legend: "",
              legendPosition: "middle",
              legendOffset: -50,
              truncateTickAt: 0,
              format: (d) => STATUSFIELD_VALUE_LABEL.confidence[d] ?? d,
              renderTick: (tick) => {
                const label = STATUSFIELD_VALUE_LABEL.confidence[tick.value] ?? tick.value;
                return (
                  <g transform={`translate(${tick.x},${tick.y - 32})`}>
                    <path d="M-28,-12 L28,-12 Q40,-12 40,0 L40,8 L0,20 L-40,8 L-40,0 Q-40,-12 -28,-12 Z" fill="#051F23" />
                    <text y={-2} textAnchor="middle" dominantBaseline="central" style={{ fill: "#FFFFFF", fontSize: 12, fontWeight: 600, fontFamily: "Plus Jakarta Sans, sans-serif" }}>
                      {label}
                    </text>
                  </g>
                );
              },
            }}
            axisLeft={{
              tickSize: 0,
              tickPadding: 0,
              tickRotation: 0,
              legend: "",
              legendPosition: "middle",
              legendOffset: -70,
              truncateTickAt: 0,
              format: (d) => STAGETYPE_LABEL[d] ?? d,
              renderTick: (tick) => {
                const label = STAGETYPE_LABEL[tick.value] ?? tick.value;
                return (
                  <g transform={`translate(${tick.x - 60},${tick.y})`}>
                    <path d="M-43,-12 L43,-12 L55,0 L43,12 L-43,12 Q-55,12 -55,0 Q-55,-12 -43,-12 Z" fill="#85C22A" />
                    <text x={-6} textAnchor="middle" dominantBaseline="central" style={{ fill: "#FFFFFF", fontSize: 12, fontWeight: 600, fontFamily: "Plus Jakarta Sans, sans-serif" }}>
                      {label}
                    </text>
                  </g>
                );
              },
            }}
            axisRight={{
              tickSize: 0,
              tickPadding: 0,
              tickValues: [],
            }}
            axisBottom={{
              tickSize: 0,
              tickPadding: 0,
              tickValues: [],
            }}
            colors={(d) => d.data.y === 0 ? "#F7FAFC" : d.data.color}
            labelTextColor={(d) => d.data.y === 0 ? "#E2E8F0" : d.data.textColor}
            onClick={(data) => {
              if (data?.value === 0) return;
              handleClick(data?.serieId, data?.data?.x);
            }}
            borderWidth={2}
            borderColor="#FFFFFF"
            tooltip={(data) => {
              return (
                <CustomTooltip
                  stageType={data?.cell?.serieId}
                  confidence={data?.cell?.data?.x}
                  value={data?.cell?.value}
                />
              );
            }}
            emptyColor="#555555"
            theme={nivo_theme}
          />
        </Box>

        {/* Vertical "Complete" arrow on right side */}
        <Flex direction="column" align="center" gap={2} pt="60px" pb="20px">
          <ArrowUp size={14} color="#85C22A" weight="bold" />
          <Box flex="1" w="1px" bg="#85C22A" />
          <Text fontSize={14} fontWeight="bold" whiteSpace="nowrap" color="lime" sx={{ writingMode: "vertical-rl", transform: "rotate(180deg)" }}>
            Complete
          </Text>
        </Flex>
      </Flex>

      {/* Horizontal "Confidence" arrow below chart */}
      <Flex align="center" ml="120px" mr="40px" gap={2} mt={-4}>
        <Text fontSize={14} fontWeight="bold" whiteSpace="nowrap" color="darkTeal">Confidence</Text>
        <Box flex="1" h="1px" bg="gray.400" />
        <ArrowRight size={14} color="#A0AEC0" weight="bold" />
      </Flex>
    </Flex>
  );
};

export default GapAnalysis;
